import { Button, Icon, Notification, type NotificationItem } from '@eleo-rh/core-ui'
import BinaryDownloadLink from 'components/Binary/BinaryDownloadLink'
import Spin from 'components/UI/Spin'
import { API_URL_ASYNC_EXPORT } from 'constants/api'
import {
  API_NOTIFICATIONS,
  API_NOTIFICATIONS_COUNT,
  API_NOTIFICATIONS_MARK_ALL_AS_READ,
  API_NOTIFICATIONS_MARK_AS_READ,
  API_NOTIFICATIONS_MARK_AS_UNREAD,
} from 'constants/api-v2'
import { useAuth } from 'context/AuthProvider'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useQuery } from 'context/QueryProvider'
import dayjs from 'dayjs'
import { useMercure, useMercureData } from 'hooks/mercure'
import useSWR from 'hooks/swr'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isPagedCollection } from 'services/apiPlatform'
import { urlAddParams } from 'services/url'
import type { Notification as NotificationType, PagedCollection } from 'shared/types'
import type { KeyedMutator } from 'swr'

type MercureData = {
  type: string
}

const Notifications = () => {
  const { profile } = useProfile()
  const { user } = useAuth()
  const { i18n } = useI18n()
  const { client } = useQuery()
  const { subscribe } = useMercure()

  const [notificationsCount, setNotificationsCount] = useState<number>(0)
  const [opened, setOpened] = useState<boolean>(false)
  const [notificationsResponse, setNotificationsResponse] = useState<undefined | PagedCollection<NotificationType>>(
    undefined,
  )
  const entityChannels = useMemo(() => ['/users', `/users/${user?.id}`], [user?.id])
  const notificationList = useMemo(() => urlAddParams(API_NOTIFICATIONS, { view: 'unread', itemsPerPage: 10 }), [])

  const notifications = useMercureData<undefined | PagedCollection<NotificationType>>(
    entityChannels,
    notificationsResponse,
  )

  useSWR(urlAddParams(API_NOTIFICATIONS_COUNT, { view: 'unread' }), {
    onSuccess: (data: { count: number }) => setNotificationsCount(data.count),
  })

  const {
    mutate,
    isValidating,
  }: {
    data: undefined | PagedCollection<NotificationType>
    mutate: KeyedMutator<PagedCollection<NotificationType>>
    isValidating: boolean
  } = useSWR(opened ? notificationList : null, {
    onSuccess: (d: PagedCollection<NotificationType>) => {
      setNotificationsResponse(d)
    },
  })

  useEffect(() => {
    if (!user) {
      return
    }

    const channels = ['/users', `/users/${user.id}`, ...(profile?.id ? [`/profiles/${profile.id}`] : [])]

    const topics = [API_NOTIFICATIONS]

    const messageEventSource = subscribe(channels, topics, ({ data }: { data: MercureData }) => {
      switch (data.type) {
        case 'new_notification':
          mutate()
          setNotificationsCount(count => count + 1)
          break
        default:
          break
      }
    })

    return () => messageEventSource?.close()
  }, [subscribe, profile?.id, user, mutate])

  const onUpdateNotification = useCallback((notification: NotificationType) => {
    setNotificationsCount(count => Math.max(0, count + (notification.read ? -1 : 1)))
    setNotificationsResponse(prevState => {
      if (prevState && isPagedCollection<NotificationType>(prevState) && prevState['hydra:member']) {
        const index = prevState['hydra:member'].findIndex((item: NotificationType) => item.id === notification.id)
        if (index !== -1) {
          return {
            ...prevState,
            'hydra:member': [
              ...prevState['hydra:member'].slice(0, index),
              notification,
              ...prevState['hydra:member'].slice(index + 1),
            ],
          }
        }
      }

      return prevState
    })
  }, [])

  const removeNotification = useCallback((notificationId: NotificationType['id']) => {
    setNotificationsResponse(prevState => {
      if (prevState && isPagedCollection<NotificationType>(prevState) && prevState['hydra:member']) {
        return {
          ...prevState,
          'hydra:member': prevState['hydra:member'].filter(item => item.id !== notificationId),
        }
      }

      return prevState
    })
  }, [])

  const markAllAsRead = async () => {
    await client.patch(API_NOTIFICATIONS_MARK_ALL_AS_READ, {})
    setNotificationsCount(0)
    setNotificationsResponse(prevState => {
      if (prevState && isPagedCollection<NotificationType>(prevState) && prevState['hydra:member']) {
        return {
          ...prevState,
          'hydra:member': prevState['hydra:member'].map(item => ({
            ...item,
            read: true,
          })),
        }
      }

      return prevState
    })
  }

  const toggleRead = async (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    notification: NotificationType,
    marAsRead?: boolean,
  ) => {
    event.stopPropagation()
    const url = marAsRead ? API_NOTIFICATIONS_MARK_AS_READ : API_NOTIFICATIONS_MARK_AS_UNREAD

    const response = await client.patch(url.replace(':id', String(notification.id)))
    onUpdateNotification(response)
  }

  const notificationsList: NotificationItem[] = (notifications?.['hydra:member'] || []).map(notification => {
    const exportName = notification.type === 'export' ? notification.context?.exportName : undefined
    const exportCreatedAt =
      notification.type === 'export' && (notification.context?.exportTaskCreatedAt || notification.createdAt)
        ? dayjs(notification.context?.exportTaskCreatedAt || notification.createdAt).format('DD/MM/YYYY HH:mm')
        : undefined
    const exportDocumentUrl =
      notification.type === 'export' && notification.context?.success && notification.document?.url
        ? notification.context.exportTaskId
          ? API_URL_ASYNC_EXPORT.replace('ID', String(notification.context.exportTaskId))
          : notification.document.url
        : undefined
    const description =
      notification.type === 'export'
        ? notification.context?.success === false
          ? i18n('notification.export.error_description', {
              error: notification.context?.error ?? i18n('internal.error'),
            })
          : notification.document?.url
            ? i18n('notification.export.description', { documentName: notification.document?.name })
            : i18n('notification.export.expired')
        : i18n(`notification.${notification.type}.description`, { version: notification.context.version })

    return {
      key: String(notification['@id']),
      title:
        notification.type === 'export' && exportName && exportCreatedAt
          ? i18n('notification.export.title_with_name_and_date', { name: exportName, date: exportCreatedAt })
          : notification.type === 'export' && exportCreatedAt
            ? i18n('notification.export.title_with_date', { date: exportCreatedAt })
          : i18n(`notification.${notification.type}.title`),
      read: notification.read,
      description: (
        <div className="flex min-w-0 flex-col gap-3">
          <p className="min-w-0 break-all">{description}</p>
          <div className="flex flex-wrap gap-2">
            {exportDocumentUrl ? (
              <BinaryDownloadLink
                href={exportDocumentUrl}
                buttonType="secondary"
                size="small"
                icon={<Icon name="arrow-down-to-bracket" />}
              >
                {i18n('label.download')}
              </BinaryDownloadLink>
            ) : null}
            <Button
              variant="tertiary"
              size="small"
              onClick={event => toggleRead(event, notification, !notification.read)}
            >
              {notification.read ? i18n('action.mark_as_unread') : i18n('action.mark_as_read')}
            </Button>
          </div>
        </div>
      ),
      onClose: async () => {
        if (!notification.read) {
          const response = await client.patch(API_NOTIFICATIONS_MARK_AS_READ.replace(':id', String(notification.id)))
          onUpdateNotification(response)
        }

        removeNotification(notification.id)
      },
      badgeIcon: 'bookmark',
    }
  })

  return (
    <Notification
      headerAction={
        isValidating || !notificationsList.length
          ? undefined
          : {
              label: i18n('action.mark_all_as_read'),
              onClick: markAllAsRead,
            }
      }
      notificationButtonProps={{
        size: 'middle',
      }}
      count={notificationsCount}
      notifications={notificationsList}
      menu={{
        onOpenChange: (nextOpened: boolean) => setOpened(nextOpened),
        trigger: ['click'],
        popupRender: menu =>
          isValidating ? (
            <div className="rounded-[4px] w-80 bg-white p-4 shadow-sm">
              <Spin size="middle" />
            </div>
          ) : !notificationsList.length ? (
            <div className="rounded-[4px] bg-white shadow-sm">
              <div className="flex items-center justify-center p-4 text-sm text-slate-500">
                {i18n('notification.no_unread')}
              </div>
            </div>
          ) : (
            menu
          ),
      }}
    />
  )
}

export default Notifications
